import React, { memo } from 'react';

import PropTypes from 'prop-types';

import localization from '../../../../shared/strings';
import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import ProductName from '../../ProductName';
import { aggregatedStringFieldPropType } from '../helpers/aggregatedFieldPropTypes';
import { isAggregatedIdentityFieldEditable } from '../helpers/aggregatedFieldHelpers';
import { resolveAggregatedFieldTooltip } from '../helpers/resolveAggregatedFieldTooltip';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import AggregatedScalarField from './AggregatedScalarField';

const l10n = localization.PRODUCT_INFO;
const aggregatedL10n = l10n.aggregated;

const AggregatedProductName = ({
  field,
  readOnly,
  readOnlyTooltip,
  isDiff,
}) => {
  const fieldLabel = l10n.autoMappedFieldLabels.title;
  const bindings = useBindAggregatedField('title', field, fieldLabel);

  if (!field || field.state === AGGREGATED_FIELD_STATE.NOT_APPLICABLE) return null;

  const isMultiple = field.state === AGGREGATED_FIELD_STATE.MULTIPLE;
  const isIdentityEditable = isAggregatedIdentityFieldEditable(field);
  const isReadOnly = readOnly || !isIdentityEditable;
  const value = field.state === AGGREGATED_FIELD_STATE.COMMON ? (field.value ?? '') : '';
  const tooltip = resolveAggregatedFieldTooltip({
    field,
    layerReadOnly: readOnly,
    layerReadOnlyTooltip: readOnlyTooltip,
    isIdentity: true,
    canSet: !isReadOnly,
    canClear: false,
  });

  if (isMultiple) {
    return (
      <AggregatedScalarField
        label={fieldLabel}
        value=""
        placeholder={aggregatedL10n.multipleValues}
        readOnly
        tooltip={tooltip}
      />
    );
  }

  return (
    <ProductName
      value={value}
      readOnly={isReadOnly}
      readOnlyTooltip={isReadOnly ? readOnlyTooltip : null}
      tooltip={tooltip}
      isDiff={isDiff}
      onChange={isReadOnly ? undefined : bindings.onChange}
    />
  );
};

AggregatedProductName.propTypes = {
  field: aggregatedStringFieldPropType,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  isDiff: PropTypes.bool,
};

AggregatedProductName.defaultProps = {
  field: null,
  readOnly: false,
  readOnlyTooltip: null,
  isDiff: false,
};

export default memo(AggregatedProductName);
